import React from "react";
import { createUseStyles } from "react-jss";

const useStyles = createUseStyles({
  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 4,
    pointerEvents: "none",
    overflow: "hidden",
    transition: ({ prefersReducedMotion }) =>
      prefersReducedMotion ? "none" : "opacity 450ms ease-in-out",
    opacity: ({ isVisible }) => (isVisible ? 1 : 0),
  },
  spotlight: {
    position: "absolute",
    inset: 0,
    background: ({ u, v }) =>
      `radial-gradient(circle clamp(140px, 22vmax, 320px) at ${u}% ${v}%, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.08) 55%, rgba(15, 23, 42, 0.62) 100%)`,
  },
  pulseWrapper: {
    position: "absolute",
    left: ({ u }) => `${u}%`,
    top: ({ v }) => `${v}%`,
    transform: "translate(-50%, -50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pulseCore: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    border: "2px solid rgba(15, 23, 42, 0.8)",
    boxShadow: "0 0 12px 2px #38BDF8",
    zIndex: 2,
  },
  pulseRingInner: {
    position: "absolute",
    width: 64,
    height: 64,
    borderRadius: "50%",
    border: "2px solid rgba(56, 189, 248, 0.75)",
    boxShadow: "0 0 16px rgba(56, 189, 248, 0.4)",
    animation: ({ prefersReducedMotion }) =>
      prefersReducedMotion ? "none" : "$kenBurnsPulseInner 2.4s cubic-bezier(0.2, 0.8, 0.2, 1) infinite",
  },
  pulseRingOuter: {
    position: "absolute",
    width: 110,
    height: 110,
    borderRadius: "50%",
    border: "1.5px solid rgba(56, 189, 248, 0.55)",
    boxShadow: "0 0 24px rgba(56, 189, 248, 0.3)",
    animation: ({ prefersReducedMotion }) =>
      prefersReducedMotion ? "none" : "$kenBurnsPulseOuter 2.4s cubic-bezier(0.2, 0.8, 0.2, 1) 0.5s infinite",
  },
  blurLayer: {
    position: "absolute",
    inset: 0,
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    maskImage: ({ u, v }) =>
      `radial-gradient(circle clamp(130px, 20vmax, 280px) at ${u}% ${v}%, transparent 0%, transparent 50%, rgba(0,0,0,0.65) 78%, black 100%)`,
    WebkitMaskImage: ({ u, v }) =>
      `radial-gradient(circle clamp(130px, 20vmax, 280px) at ${u}% ${v}%, transparent 0%, transparent 50%, rgba(0,0,0,0.65) 78%, black 100%)`,
  },
  lensVignette: {
    position: "absolute",
    inset: 0,
    background: ({ u, v }) =>
      `radial-gradient(circle 140px at ${u}% ${v}%, transparent 0%, transparent 138px, rgba(15, 23, 42, 0.45) 142px)`,
  },
  lensRing: {
    position: "absolute",
    width: 280,
    height: 280,
    left: ({ u }) => `${u}%`,
    top: ({ v }) => `${v}%`,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    border: "2.5px solid rgba(255, 255, 255, 0.88)",
    boxShadow:
      "0 0 0 1px rgba(15, 23, 42, 0.5), 0 12px 32px rgba(0, 0, 0, 0.45), inset 0 0 20px rgba(255, 255, 255, 0.22)",
  },
  "@keyframes kenBurnsPulseInner": {
    "0%": { transform: "scale(0.85)", opacity: 0.85 },
    "50%": { transform: "scale(1.15)", opacity: 0.3 },
    "100%": { transform: "scale(0.85)", opacity: 0.85 },
  },
  "@keyframes kenBurnsPulseOuter": {
    "0%": { transform: "scale(0.88)", opacity: 0.65 },
    "50%": { transform: "scale(1.2)", opacity: 0.15 },
    "100%": { transform: "scale(0.88)", opacity: 0.65 },
  },
});

const clampCoord = (val) => Math.max(-1, Math.min(1, val ?? 0));

export const KenBurnsFocusEffectOverlay = ({
  focus,
  active = true,
  prefersReducedMotion = false,
}) => {
  const effect = focus?.effect;
  const isVisible = Boolean(active && effect && effect !== "none");

  const x = clampCoord(focus?.x);
  const y = clampCoord(focus?.y);
  const u = ((x + 1) / 2) * 100;
  const v = ((1 - y) / 2) * 100;

  const classes = useStyles({ u, v, isVisible, prefersReducedMotion });

  if (!effect || effect === "none") {
    return null;
  }

  return (
    <div
      className={classes.overlay}
      data-testid="kenburns-focus-overlay"
      data-effect={effect}
      aria-hidden="true"
    >
      {effect === "spotlight" && (
        <div className={classes.spotlight} data-testid="kenburns-effect-spotlight" />
      )}
      {effect === "pulse" && (
        <div className={classes.pulseWrapper} data-testid="kenburns-effect-pulse">
          <div className={classes.pulseRingOuter} />
          <div className={classes.pulseRingInner} />
          <div className={classes.pulseCore} />
        </div>
      )}
      {effect === "blur" && (
        <div className={classes.blurLayer} data-testid="kenburns-effect-blur" />
      )}
      {effect === "lens" && (
        <div data-testid="kenburns-effect-lens">
          <div className={classes.lensVignette} />
          <div className={classes.lensRing} />
        </div>
      )}
    </div>
  );
};
